import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  CircleDashed,
  Database,
  Link2,
  MessageSquareHeart,
  ShieldCheck,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { api } from '../lib/api';
import { getApiBase } from '../lib/apiBase';
import { useAuth } from '../contexts/AuthContext';
import { usePilotContext } from '../hooks/usePilotContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import MagicBento from '../components/MagicBento';
import { ReportAssistPanel } from '../components/ReportAssistPanel';

const SEVERITY_COLORS = {
  Critical: '#ef4444',
  High: '#f97316',
  Medium: '#eab308',
  Low: '#22c55e',
};

const CHART_TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: 'rgba(24, 24, 27, 0.95)',
    border: '1px solid #3f3f46',
    borderRadius: '8px',
    fontSize: '12px',
  },
  labelStyle: { color: '#fafafa' },
};

const DASHBOARD_POLICY_FILTER_KEY = 'aerta_dashboard_policy_decision_filter_v1';
const DASHBOARD_POLICY_SEARCH_KEY = 'aerta_dashboard_policy_decision_search_v1';

export function DashboardPage() {
  const { context } = usePilotContext();
  const { submitPilotFeedback } = useAuth();
  const [system, setSystem] = useState(null);
  const [risk, setRisk] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [learning, setLearning] = useState(null);
  const [learningHistory, setLearningHistory] = useState([]);
  const [learningTasks, setLearningTasks] = useState(null);
  const [policyQueue, setPolicyQueue] = useState(null);
  const [loopStatus, setLoopStatus] = useState(null);
  const [applySuppressions, setApplySuppressions] = useState(false);
  /** Core overview (system + risk) — when false, main UI renders (Report assist, etc.). */
  const [loading, setLoading] = useState(true);
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [policyActionBusyId, setPolicyActionBusyId] = useState(null);
  const [taskActionBusyId, setTaskActionBusyId] = useState(null);
  const [decisionFilter, setDecisionFilter] = useState('all');
  const [decisionHistoryOpen, setDecisionHistoryOpen] = useState(false);
  const [decisionSearch, setDecisionSearch] = useState('');
  const [feedbackRating, setFeedbackRating] = useState('');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [showFirstRunGuide, setShowFirstRunGuide] = useState(false);
  const [pppSummary, setPppSummary] = useState(null);
  const [protectScorecard, setProtectScorecard] = useState(null);
  const [devguardScans, setDevguardScans] = useState([]);
  const [pppBundleBusy, setPppBundleBusy] = useState(false);
  const [govExportBusy, setGovExportBusy] = useState(false);
  const [execHtmlBusy, setExecHtmlBusy] = useState(false);
  const [devguardQuickBusy, setDevguardQuickBusy] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem('aerta_dashboard_guide_dismissed_v1') === '1';
      setShowFirstRunGuide(!dismissed);
    } catch {
      setShowFirstRunGuide(true);
    }
  }, []);

  useEffect(() => {
    try {
      const savedFilter = localStorage.getItem(DASHBOARD_POLICY_FILTER_KEY);
      const savedSearch = localStorage.getItem(DASHBOARD_POLICY_SEARCH_KEY);
      if (savedFilter === 'all' || savedFilter === 'approved' || savedFilter === 'rejected') {
        setDecisionFilter(savedFilter);
      }
      if (typeof savedSearch === 'string') {
        setDecisionSearch(savedSearch);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_POLICY_FILTER_KEY, decisionFilter);
    } catch {}
  }, [decisionFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_POLICY_SEARCH_KEY, decisionSearch);
    } catch {}
  }, [decisionSearch]);

  useEffect(() => {
    async function load() {
      if (!context.customer_id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const riskQs = applySuppressions ? '?apply_suppressions=true' : '';
      const cid = context.customer_id;
      try {
        const [systemData, riskData] = await Promise.allSettled([
          api.get('/api/system/status', { timeoutMs: 25000 }),
          api.get(`/api/risk/${cid}/summary${riskQs}`, { timeoutMs: 60000 }),
        ]);
        if (systemData.status === 'fulfilled') setSystem(systemData.value);
        else setSystem(null);
        if (riskData.status === 'fulfilled') setRisk(riskData.value);
        else setRisk(null);
      } catch (e) {
        console.error('Failed to load overview:', e);
      } finally {
        setLoading(false);
      }

      setExtrasLoading(true);
      try {
        const results = await Promise.allSettled([
          api.get(`/api/forecasts/${cid}/latest`, { timeoutMs: 30000 }),
          api.get(`/api/metrics/${cid}/overview`, { timeoutMs: 30000 }),
          api.get(`/api/pilot/verification-loop/${cid}`, { timeoutMs: 30000 }),
          api.get(`/api/learning/${cid}/overview`, { timeoutMs: 30000 }),
          api.get(`/api/learning/${cid}/history?points=40`, { timeoutMs: 30000 }),
          api.get(`/api/learning/${cid}/policy/queue`, { timeoutMs: 30000 }),
          api.get(`/api/learning/${cid}/tasks`, { timeoutMs: 30000 }),
          api.get(`/api/golden-path/${cid}/ppp/summary?activity_days=7`, { timeoutMs: 20000 }),
          api.get(`/api/golden-path/${cid}/protect/scorecard`, { timeoutMs: 20000 }),
          api.get('/api/operations-hub/devguard/scans?limit=10', { timeoutMs: 20000 }),
        ]);
        const [f, m, l, le, lh, pq, lt, ppp, psc, dgs] = results;
        if (f.status === 'fulfilled') setForecast(f.value);
        else setForecast(null);
        if (m.status === 'fulfilled') setMetrics(m.value);
        else setMetrics(null);
        if (l.status === 'fulfilled') setLoopStatus(l.value);
        else setLoopStatus(null);
        if (le.status === 'fulfilled') setLearning(le.value);
        else setLearning(null);
        if (lh.status === 'fulfilled') setLearningHistory(lh.value?.series || []);
        else setLearningHistory([]);
        if (pq.status === 'fulfilled') setPolicyQueue(pq.value);
        else setPolicyQueue(null);
        if (lt.status === 'fulfilled') setLearningTasks(lt.value);
        else setLearningTasks(null);
        if (ppp.status === 'fulfilled') setPppSummary(ppp.value);
        else setPppSummary(null);
        if (psc.status === 'fulfilled') setProtectScorecard(psc.value);
        else setProtectScorecard(null);
        if (dgs.status === 'fulfilled') setDevguardScans(dgs.value?.items || []);
        else setDevguardScans([]);
      } catch (e) {
        console.error('Failed to load overview extras:', e);
      } finally {
        setExtrasLoading(false);
      }
    }
    load();
  }, [context.customer_id, applySuppressions]);

  async function downloadExecutiveBundle() {
    const cid = context.customer_id;
    if (!cid) return;
    setPppBundleBusy(true);
    try {
      const data = await api.get(
        `/api/golden-path/${encodeURIComponent(cid)}/executive-bundle?activity_days=7&ranking=rules`,
        { timeoutMs: 45000 }
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aerta-executive-bundle-${cid}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Executive bundle downloaded');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setPppBundleBusy(false);
    }
  }

  async function downloadExecutiveReportHtml() {
    const cid = context.customer_id;
    if (!cid) return;
    setExecHtmlBusy(true);
    try {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('aerta_token') : null;
      const base = getApiBase();
      const url = `${base}/api/golden-path/${encodeURIComponent(cid)}/executive-report/html?activity_days=7&ranking=rules`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText || String(res.status));
      }
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `aerta-executive-golden-path-${cid}.html`;
      a.click();
      URL.revokeObjectURL(href);
      toast.success('Executive HTML report downloaded');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setExecHtmlBusy(false);
    }
  }

  async function downloadGovernanceExport() {
    const cid = context.customer_id;
    if (!cid) return;
    setGovExportBusy(true);
    try {
      const data = await api.get(
        `/api/golden-path/${encodeURIComponent(cid)}/governance-export?audit_sample_limit=50`,
        { timeoutMs: 45000 }
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aerta-governance-export-${cid}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Governance export downloaded');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setGovExportBusy(false);
    }
  }

  async function quickCreateAndRunDevGuard() {
    setDevguardQuickBusy(true);
    try {
      const created = await api.post(
        '/api/operations-hub/devguard/scans',
        {
          website_url: window?.location?.origin || null,
          scanners: ['sast', 'deps', 'secrets', 'web_dast', 'headers'],
          audience: 'engineer',
          note: 'Dashboard quick-start scan',
        },
        { timeoutMs: 30000 }
      );
      const scanId = created?.scan_id;
      if (scanId) {
        await api.post(
          `/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/run`,
          { force: true },
          { timeoutMs: 30000 }
        );
      }
      const latest = await api.get('/api/operations-hub/devguard/scans?limit=10', { timeoutMs: 20000 });
      setDevguardScans(latest?.items || []);
      toast.success('DevGuard scan created and executed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setDevguardQuickBusy(false);
    }
  }

  const severityData = useMemo(() => {
    if (!risk?.has_report) return [];
    const rows = [
      { name: 'Critical', value: risk.critical_count ?? 0, color: SEVERITY_COLORS.Critical },
      { name: 'High', value: risk.high_count ?? 0, color: SEVERITY_COLORS.High },
      { name: 'Medium', value: risk.medium_count ?? 0, color: SEVERITY_COLORS.Medium },
      { name: 'Low', value: risk.low_count ?? 0, color: SEVERITY_COLORS.Low },
    ];
    return rows.filter((d) => d.value > 0);
  }, [risk]);

  const threatBarData = useMemo(() => {
    const threats = forecast?.threats || [];
    return threats.slice(0, 10).map((t) => ({
      name:
        (t.attack_name || t.path_id || 'Threat').length > 24
          ? `${(t.attack_name || t.path_id).slice(0, 22)}…`
          : t.attack_name || t.path_id || 'Threat',
      fullName: t.attack_name || t.path_id,
      likelihoodPct: Math.round((t.likelihood ?? 0) * 1000) / 10,
      domain: t.domain || '',
    }));
  }, [forecast]);

  const defenseBarData = useMemo(() => {
    const score = Math.min(100, Math.max(0, Number(forecast?.defense_score ?? 0)));
    return [{ name: 'Defense posture (0–100)', score }];
  }, [forecast]);

  const topPendingPolicyItem = useMemo(() => {
    const items = Array.isArray(policyQueue?.items) ? policyQueue.items : [];
    return items.find((it) => it?.status === 'pending_approval') || null;
  }, [policyQueue]);

  const recentPolicyDecisions = useMemo(() => {
    const rows = Array.isArray(policyQueue?.recent_decisions) ? policyQueue.recent_decisions : [];
    return rows.slice(0, 5);
  }, [policyQueue]);

  const filteredRecentPolicyDecisions = useMemo(() => {
    const rows = Array.isArray(policyQueue?.recent_decisions) ? policyQueue.recent_decisions : [];
    const statusFiltered =
      decisionFilter === 'approved'
        ? rows.filter((r) => r?.status === 'approved')
        : decisionFilter === 'rejected'
          ? rows.filter((r) => r?.status === 'rejected')
          : rows;
    const q = decisionSearch.trim().toLowerCase();
    if (!q) return statusFiltered;
    return statusFiltered.filter((r) =>
      [r?.recommendation_id, r?.task_type, r?.approved_by, r?.rejected_by, r?.created_by, r?.status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [policyQueue, decisionFilter, decisionSearch]);

  const latestDevguard = useMemo(() => (devguardScans || [])[0] || null, [devguardScans]);
  const devguardTopActions = useMemo(() => {
    const findings = latestDevguard?.result?.findings || [];
    const recs = [];
    for (const f of findings) {
      const r = String(f?.recommendation || '').trim();
      if (r && !recs.includes(r)) recs.push(r);
      if (recs.length >= 3) break;
    }
    return recs;
  }, [latestDevguard]);

  const updatePolicyQueueItem = (itemId, nextStatus) => {
    setPolicyQueue((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      const updatedItems = prev.items.map((it) => (it?.id === itemId ? { ...it, status: nextStatus } : it));
      const nextPending = updatedItems.filter((it) => it?.status === 'pending_approval').length;
      return {
        ...prev,
        items: updatedItems,
        pending: nextPending,
      };
    });
  };

  const approveDashboardPolicyItem = async (itemId) => {
    if (!context?.customer_id || !itemId) return;
    setPolicyActionBusyId(`approve:${itemId}`);
    try {
      await api.post(`/api/learning/${context.customer_id}/policy/queue/${itemId}/approve`, {
        note: 'Approved from Dashboard',
      });
      updatePolicyQueueItem(itemId, 'approved');
      toast.success('Policy action approved');
    } catch (e) {
      toast.error(e.message || 'Could not approve policy action');
    } finally {
      setPolicyActionBusyId(null);
    }
  };

  const rejectDashboardPolicyItem = async (itemId) => {
    if (!context?.customer_id || !itemId) return;
    setPolicyActionBusyId(`reject:${itemId}`);
    try {
      await api.post(`/api/learning/${context.customer_id}/policy/queue/${itemId}/reject`, {
        note: 'Rejected from Dashboard',
      });
      updatePolicyQueueItem(itemId, 'rejected');
      toast.success('Policy action rejected');
    } catch (e) {
      toast.error(e.message || 'Could not reject policy action');
    } finally {
      setPolicyActionBusyId(null);
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    if (!context?.customer_id || !taskId) return;
    setTaskActionBusyId(`${status}:${taskId}`);
    try {
      await api.post(`/api/learning/${context.customer_id}/tasks/${taskId}/status`, {
        status,
        note: 'Updated from Dashboard',
      });
      setLearningTasks((prev) => {
        if (!prev || !Array.isArray(prev.items)) return prev;
        const items = prev.items.map((it) => (it?.id === taskId ? { ...it, status } : it));
        return {
          ...prev,
          items,
          ready: items.filter((it) => it?.status === 'ready').length,
          in_progress: items.filter((it) => it?.status === 'in_progress').length,
          done: items.filter((it) => it?.status === 'done').length,
        };
      });
      toast.success(`Task marked ${status.replace('_', ' ')}`);
    } catch (e) {
      toast.error(e.message || 'Could not update task status');
    } finally {
      setTaskActionBusyId(null);
    }
  };

  const resetDecisionAuditFilters = () => {
    setDecisionFilter('all');
    setDecisionSearch('');
    try {
      localStorage.removeItem(DASHBOARD_POLICY_FILTER_KEY);
      localStorage.removeItem(DASHBOARD_POLICY_SEARCH_KEY);
    } catch {}
    toast.success('Audit filters reset');
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="animate-fade-in-up motion-reduce:animate-none">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Pilot Overview</h1>
        <p className="text-muted-foreground">
          Tenant{' '}
          <span className="font-medium text-foreground">
            {context.customer_label || context.customer_id}
          </span>
          <span className="mx-1.5 text-border">·</span>
          <span>{context.sector_label || context.sector}</span>
          <span className="text-muted-foreground"> sector</span>
          {extrasLoading ? (
            <span className="ml-2 text-xs text-muted-foreground">· loading forecasts &amp; loop…</span>
          ) : null}
        </p>
        <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={applySuppressions}
            onChange={(e) => setApplySuppressions(e.target.checked)}
          />
          Risk summary respects finding suppressions
        </label>
        <p className="mt-2 text-xs text-muted-foreground">
          Security bulletins and recent sensor alerts are in the{' '}
          <span className="font-medium text-foreground">bell</span> (top right).
        </p>
      </div>

      {showFirstRunGuide ? (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Start here (first-time setup)</CardTitle>
            <CardDescription>
              Run this path once to make every AERTA feature produce meaningful output.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal space-y-1 pl-4 text-foreground">
              <li>Ingest assets in `Assets` (and optional `Net scan`).</li>
              <li>Run `Risk`, then open `Forecasts` and `Graph findings`.</li>
              <li>Generate in `Remediation`, then run `Resimulation`.</li>
              <li>Open `Compliance` and `Reports` for attestation outputs.</li>
              <li>Track AI-vs-AI progression in `Cyber range` trend graph.</li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <Link to="/assets" className="rounded-md border border-border px-3 py-1.5 hover:bg-secondary">
                Open Assets
              </Link>
              <Link to="/risk" className="rounded-md border border-border px-3 py-1.5 hover:bg-secondary">
                Open Risk
              </Link>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  try {
                    localStorage.setItem('aerta_dashboard_guide_dismissed_v1', '1');
                  } catch {}
                  setShowFirstRunGuide(false);
                }}
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="aerta-stagger grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
      <div>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                Pilot — verified fix loop
              </CardTitle>
              <CardDescription>
                Discovery → risk → paths → remediation → resimulation → attestation. Complete each step
                for a defensible pilot story.
              </CardDescription>
            </div>
            {loopStatus ? (
              <span
                className={
                  loopStatus.loop_complete
                    ? 'rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600'
                    : 'rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-800'
                }
              >
                {loopStatus.loop_complete ? 'Ready' : 'In progress'}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">Status unavailable</span>
            )}
          </CardHeader>
          <CardContent>
            {loopStatus?.steps?.length ? (
              <ul className="mb-4 grid gap-2 sm:grid-cols-2">
                {loopStatus.steps.map((s) => {
                  const ok = s.status === 'ok';
                  return (
                    <li
                      key={s.id}
                      className="flex items-start gap-2 rounded-md border border-border/80 bg-secondary/15 px-2 py-1.5 text-xs"
                    >
                      {ok ? (
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                      ) : (
                        <CircleDashed className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span>
                        <span className="font-medium capitalize text-foreground">
                          {s.id.replace(/_/g, ' ')}
                        </span>
                        <span className="block text-muted-foreground">{s.detail}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mb-4 text-sm text-muted-foreground">Load the API to see checklist steps.</p>
            )}
            <Link
              to="/verification-loop"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              Open full fix loop page
              <ArrowRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick actions</CardTitle>
            <CardDescription>Typical pilot sequence</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Link className="rounded-md border border-border px-3 py-2 transition-colors hover:bg-secondary" to="/assets">
              1. Ingest / review assets
            </Link>
            {context.network_scan_enabled ? (
              <Link
                className="rounded-md border border-border px-3 py-2 transition-colors hover:bg-secondary"
                to="/network-scan"
              >
                Optional: network scan (allowlisted)
              </Link>
            ) : (
              <p className="rounded-md border border-dashed border-border/60 px-3 py-2 text-xs text-muted-foreground">
                Network scan disabled — set <code className="text-[10px]">AERTA_SCAN_ENABLED=true</code> and
                allowlist for live discovery.
              </p>
            )}
            <Link className="rounded-md border border-border px-3 py-2 transition-colors hover:bg-secondary" to="/risk">
              2. Run risk &amp; explain findings
            </Link>
            <Link className="rounded-md border border-border px-3 py-2 transition-colors hover:bg-secondary" to="/remediation">
              3. Generate remediation
            </Link>
            <Link className="rounded-md border border-border px-3 py-2 transition-colors hover:bg-secondary" to="/resimulation">
              4. Resimulate paths
            </Link>
            <Link className="rounded-md border border-border px-3 py-2 transition-colors hover:bg-secondary" to="/compliance">
              5. Compliance &amp; attest
            </Link>
          </CardContent>
        </Card>
      </div>

      {context.customer_id ? (
        <Card className="border-primary/20 bg-gradient-to-br from-card to-secondary/10">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Predict · Prevent · Protect
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pppBundleBusy || govExportBusy || execHtmlBusy}
                  onClick={() => void downloadExecutiveBundle()}
                >
                  {pppBundleBusy ? 'Preparing…' : 'Executive bundle (JSON)'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={execHtmlBusy || pppBundleBusy || govExportBusy}
                  onClick={() => void downloadExecutiveReportHtml()}
                >
                  {execHtmlBusy ? 'Preparing…' : 'Executive report (HTML)'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={govExportBusy || pppBundleBusy || execHtmlBusy}
                  onClick={() => void downloadGovernanceExport()}
                >
                  {govExportBusy ? 'Preparing…' : 'Governance export (JSON)'}
                </Button>
              </span>
            </CardTitle>
            <CardDescription>
              Golden-path snapshot: rules-based priority queue (ML-ready shape), remediation counts, defense signals &
              retests.{' '}
              <span className="text-muted-foreground">
                {pppSummary?.predict?.model_version ? `Model ${pppSummary.predict.model_version}` : ''}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border/60 bg-background/50 p-3 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Predict</div>
              {extrasLoading && !pppSummary ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : (
                <>
                  <p className="text-2xl font-semibold text-foreground">
                    {(pppSummary?.predict?.queue || []).length ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground">ranked remediations ({pppSummary?.predict?.ranking_method || '—'})</p>
                  {(pppSummary?.predict?.queue || []).slice(0, 3).map((row) => (
                    <div key={row.remediation_id} className="mt-2 border-t border-border/40 pt-2 text-xs">
                      <span className="font-mono text-[11px]">{row.finding_id}</span>
                      <span className="ml-2 text-muted-foreground">score {row.score}</span>
                    </div>
                  ))}
                  {pppSummary?.predict?.drift_hint?.status ? (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Ranker signal: {String(pppSummary.predict.drift_hint.status)}
                      {pppSummary.predict.active_model_id
                        ? ` · ${String(pppSummary.predict.active_model_id)}`
                        : ''}
                    </p>
                  ) : null}
                </>
              )}
            </div>
            <div className="rounded-lg border border-border/60 bg-background/50 p-3 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prevent</div>
              <p className="text-2xl font-semibold text-foreground">
                {pppSummary?.prevent?.remediations_total ?? '—'}
              </p>
              <p className="text-xs text-muted-foreground">total remediations</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Last {pppSummary?.activity_window_days ?? 7}d: {pppSummary?.activity?.new_remediations ?? 0} new
              </p>
            </div>
            <div className="rounded-lg border border-border/60 bg-background/50 p-3 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Protect</div>
              <p className="text-2xl font-semibold text-foreground">
                {pppSummary?.protect?.defense_signals_total ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">defense signals</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Retest pass: {pppSummary?.protect?.retest_pass_count ?? 0} · fail:{' '}
                {pppSummary?.protect?.retest_fail_count ?? 0} · cleared: {pppSummary?.protect?.cleared_via_workflow ?? 0}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                New signals ({pppSummary?.activity_window_days ?? 7}d): {pppSummary?.activity?.new_defense_signals ?? 0}
              </p>
              {protectScorecard?.totals ? (
                <p className="mt-2 border-t border-border/40 pt-2 text-xs text-muted-foreground">
                  Scorecard ({(protectScorecard.scenarios || []).length} scenario
                  {(protectScorecard.scenarios || []).length === 1 ? '' : 's'}
                  ){protectScorecard.totals.protect_score_0_100 != null ? (
                    <>
                      {' '}
                      · blended{' '}
                      <span className="font-medium text-foreground">
                        {protectScorecard.totals.protect_score_0_100}
                      </span>
                      /100
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
          </CardContent>
          <CardContent className="border-t border-border/40 pt-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <Link className="text-primary underline" to="/graph-findings">
                Graph findings
              </Link>
              <Link className="text-primary underline" to="/operations">
                Fusion hub
              </Link>
              <Link className="text-primary underline" to="/runs">
                Executor runs
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {context.customer_id ? (
        <Card className="border-primary/20 bg-gradient-to-br from-card to-secondary/10">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                DevGuard quick security actions
              </span>
              <Button type="button" variant="outline" size="sm" disabled={devguardQuickBusy} onClick={() => void quickCreateAndRunDevGuard()}>
                {devguardQuickBusy ? 'Running…' : 'Create + Run scan'}
              </Button>
            </CardTitle>
            <CardDescription>
              Founder/engineer-first guidance from latest DevGuard scan.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-xs text-muted-foreground">
              Latest status: {latestDevguard?.status || 'no scan yet'} · mode: {latestDevguard?.adapter_mode || '—'}
            </p>
            {devguardTopActions.length > 0 ? (
              devguardTopActions.map((a) => (
                <div key={a} className="rounded-md border border-border/60 bg-background/50 px-3 py-2">
                  {a}
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">No recommendations yet. Run a quick DevGuard scan to generate top actions.</p>
            )}
            <div className="pt-1 text-xs">
              <Link className="text-primary underline" to="/devguard">
                Open full DevGuard workflow
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <section className="rounded-xl border border-border/50 bg-card/40 p-4 shadow-sm">
        <h2 className="font-heading text-lg font-semibold text-foreground">Pilot modules</h2>
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Hover the grid for spotlight and particles — same areas as the left nav. Reduced motion and mobile disable
          heavy effects.
        </p>
        <MagicBento
          textAutoHide
          enableStars
          enableSpotlight
          enableBorderGlow
          enableTilt={false}
          enableMagnetism={false}
          clickEffect
          spotlightRadius={400}
          particleCount={12}
          glowColor="132, 0, 255"
        />
      </section>

      <div className="aerta-stagger grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="secondary">{system?.status || 'unknown'}</Badge>
            <p className="mt-2 text-sm text-muted-foreground">
              Active alerts: {system?.active_alerts_count ?? 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Risk Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-card-foreground">
              {risk?.avg_risk_score?.toFixed?.(2) ?? '0.00'}
            </p>
            <p className="text-sm text-muted-foreground">
              Assets: {risk?.total_assets ?? 0} | Critical: {risk?.critical_count ?? 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-team" />
              Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-card-foreground">{forecast?.defense_score ?? 0}</p>
            <p className="text-sm text-muted-foreground">
              Threats in window: {forecast?.threats?.length ?? 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-primary/20 bg-card/70">
        <CardHeader>
          <CardTitle>Learning engine (Red vs Blue)</CardTitle>
          <CardDescription>
            Co-evolution signal from cyber range memory and recent outcomes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {policyQueue?.pending > 0 ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
              <p className="font-medium text-foreground">
                {policyQueue.pending} high-tier learning action{policyQueue.pending > 1 ? 's' : ''} awaiting approval
              </p>
              {policyQueue.executed > 0 ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {policyQueue.executed} approved action{policyQueue.executed > 1 ? 's' : ''} already materialized into tasks.
                </p>
              ) : null}
              {topPendingPolicyItem ? (
                <div className="mt-2 rounded border border-border/70 bg-background/60 px-2.5 py-2">
                  <p className="text-xs text-muted-foreground">
                    Next: <span className="font-medium text-foreground">{topPendingPolicyItem.recommendation_id}</span> (
                    {topPendingPolicyItem.task_type || 'task'})
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      disabled={policyActionBusyId === `approve:${topPendingPolicyItem.id}`}
                      onClick={() => approveDashboardPolicyItem(topPendingPolicyItem.id)}
                    >
                      {policyActionBusyId === `approve:${topPendingPolicyItem.id}` ? 'Approving…' : 'Quick approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={policyActionBusyId === `reject:${topPendingPolicyItem.id}`}
                      onClick={() => rejectDashboardPolicyItem(topPendingPolicyItem.id)}
                    >
                      {policyActionBusyId === `reject:${topPendingPolicyItem.id}` ? 'Rejecting…' : 'Quick reject'}
                    </Button>
                  </div>
                </div>
              ) : null}
              <div className="mt-1 flex items-center gap-3">
                <p className="text-xs text-muted-foreground">
                  Review and approve/reject from AERTAGO policy queue.
                </p>
                <Link to="/fusion/cyber-range" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                  Open queue
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ) : null}
          {recentPolicyDecisions.length ? (
            <div className="rounded-md border border-border/70 bg-secondary/10 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase text-muted-foreground">Recent policy decisions</p>
                <div className="flex flex-wrap items-center gap-1">
                  <Button size="sm" variant={decisionFilter === 'all' ? 'default' : 'outline'} onClick={() => setDecisionFilter('all')}>
                    All
                  </Button>
                  <Button
                    size="sm"
                    variant={decisionFilter === 'approved' ? 'default' : 'outline'}
                    onClick={() => setDecisionFilter('approved')}
                  >
                    Approved
                  </Button>
                  <Button
                    size="sm"
                    variant={decisionFilter === 'rejected' ? 'default' : 'outline'}
                    onClick={() => setDecisionFilter('rejected')}
                  >
                    Rejected
                  </Button>
                </div>
              </div>
              <ul className="mt-2 space-y-1.5 text-xs">
                {filteredRecentPolicyDecisions.slice(0, 5).map((d) => {
                  const when = d.approved_at || d.rejected_at || d.created_at;
                  const by = d.approved_by || d.rejected_by || d.created_by || 'operator';
                  return (
                    <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded bg-background/40 px-2 py-1">
                      <span className="text-foreground">
                        {d.recommendation_id} - {d.status}
                      </span>
                      <span className="text-muted-foreground">
                        {by} · {when ? new Date(when).toLocaleString() : '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => setDecisionHistoryOpen(true)}>
                  View full history
                </Button>
              </div>
            </div>
          ) : null}
          {!learning ? (
            <p className="text-sm text-muted-foreground">Learning metrics unavailable yet.</p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border border-border bg-background/40 p-3">
                  <p className="text-xs text-muted-foreground">Red sophistication</p>
                  <p className="text-2xl font-bold">{learning.learning_indices?.red_sophistication ?? '—'}</p>
                </div>
                <div className="rounded-md border border-border bg-background/40 p-3">
                  <p className="text-xs text-muted-foreground">Blue resilience</p>
                  <p className="text-2xl font-bold">{learning.learning_indices?.blue_resilience ?? '—'}</p>
                </div>
                <div className="rounded-md border border-border bg-background/40 p-3">
                  <p className="text-xs text-muted-foreground">Net security lift</p>
                  <p className="text-2xl font-bold">{learning.learning_indices?.net_security_lift ?? '—'}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Window: {learning.recent_window?.matches_considered ?? 0} matches · Red{' '}
                {learning.recent_window?.red_win_rate_pct ?? 0}% · Blue{' '}
                {learning.recent_window?.blue_win_rate_pct ?? 0}%
              </div>
              {Array.isArray(learning.recommendations) && learning.recommendations.length > 0 ? (
                <ul className="space-y-2">
                  {learning.recommendations.slice(0, 3).map((r) => (
                    <li key={r.id} className="rounded-md border border-border/70 bg-secondary/20 px-3 py-2 text-sm">
                      <p className="font-medium text-foreground">{r.title}</p>
                      <p className="text-xs text-muted-foreground">{r.why}</p>
                      <p className="mt-1 text-xs">{r.action}</p>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Learning trend</CardTitle>
          <CardDescription>Historical Red/Blue indices from recent snapshots.</CardDescription>
        </CardHeader>
        <CardContent>
          {!learningHistory.length ? (
            <p className="text-sm text-muted-foreground">No history yet. Keep running cycles to build trend data.</p>
          ) : (
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={learningHistory}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border opacity-40" />
                  <XAxis
                    dataKey="ts"
                    tick={{ fill: '#a1a1aa', fontSize: 10 }}
                    tickFormatter={(v) => String(v || '').slice(11, 16)}
                  />
                  <YAxis domain={[0, 100]} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    labelFormatter={(v) => `Snapshot ${String(v || '').replace('T', ' ').slice(0, 19)} UTC`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="red_sophistication" name="Red sophistication" stroke="#fb7185" dot={false} />
                  <Line type="monotone" dataKey="blue_resilience" name="Blue resilience" stroke="#38bdf8" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Learning playbook tasks</CardTitle>
          <CardDescription>Concrete tasks generated from approved learning policy actions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!learningTasks?.items?.length ? (
            <p className="text-sm text-muted-foreground">No generated learning tasks yet.</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Ready {learningTasks.ready || 0} · In progress {learningTasks.in_progress || 0} · Done {learningTasks.done || 0}
              </p>
              <ul className="space-y-2">
                {learningTasks.items.slice(0, 6).map((t) => (
                  <li key={t.id} className="rounded-md border border-border/70 bg-secondary/20 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-foreground">{t.recommendation_id}</p>
                      <Badge variant={t.status === 'done' ? 'default' : 'secondary'}>{t.status || 'ready'}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{t.task_type || 'task'} · {t.id}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={taskActionBusyId === `ready:${t.id}`}
                        onClick={() => updateTaskStatus(t.id, 'ready')}
                      >
                        Ready
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={taskActionBusyId === `in_progress:${t.id}`}
                        onClick={() => updateTaskStatus(t.id, 'in_progress')}
                      >
                        In progress
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        disabled={taskActionBusyId === `done:${t.id}`}
                        onClick={() => updateTaskStatus(t.id, 'done')}
                      >
                        Done
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={decisionHistoryOpen} onOpenChange={setDecisionHistoryOpen}>
        <DialogContent className="max-h-[75vh] overflow-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Policy decision history</DialogTitle>
            <DialogDescription>Recent governance actions for learning policy queue items.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="policy-decision-search" className="text-xs font-medium text-muted-foreground">
                Search decisions
              </label>
              <Button size="sm" variant="outline" onClick={resetDecisionAuditFilters}>
                Reset audit filters
              </Button>
            </div>
            <input
              id="policy-decision-search"
              type="text"
              value={decisionSearch}
              onChange={(e) => setDecisionSearch(e.target.value)}
              placeholder="Search by recommendation, task type, or actor"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          {!filteredRecentPolicyDecisions.length ? (
            <p className="text-sm text-muted-foreground">No decisions for this filter yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {filteredRecentPolicyDecisions.map((d) => {
                const when = d.approved_at || d.rejected_at || d.created_at;
                const by = d.approved_by || d.rejected_by || d.created_by || 'operator';
                return (
                  <li key={d.id} className="rounded-md border border-border/70 bg-secondary/20 px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-foreground">{d.recommendation_id}</p>
                      <Badge variant={d.status === 'approved' ? 'default' : 'secondary'}>{d.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {d.task_type || 'task'} · {by} · {when ? new Date(when).toLocaleString() : '—'}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <div className="aerta-stagger grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-primary" />
              CMDB assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{metrics?.cmdb_asset_count ?? '—'}</p>
            <p className="text-xs text-muted-foreground">PostgreSQL inventory</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4 text-primary" />
              Last discovery file
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-sm leading-snug">
              {metrics?.last_discovery_file_at
                ? new Date(metrics.last_discovery_file_at).toLocaleString()
                : '—'}
            </p>
            <p className="text-xs text-muted-foreground">Latest scan-*.json mtime</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Resimulation jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{metrics?.resim_job_files ?? '—'}</p>
            <p className="text-xs text-muted-foreground">JSON files in resim_jobs</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Risk report</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={metrics?.risk_report_present ? 'default' : 'secondary'}>
              {metrics?.risk_report_present ? 'present' : 'missing'}
            </Badge>
            {metrics?.risk?.generated_at ? (
              <p className="mt-2 font-mono text-xs text-muted-foreground">
                {metrics.risk.generated_at}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {!loading && <ReportAssistPanel hasReport={!!risk?.has_report} compact />}

      <Card className="border-dashed border-primary/25 bg-secondary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquareHeart className="h-4 w-4 text-primary" />
            Pilot feedback
          </CardTitle>
          <CardDescription>
            Optional — helps us prioritize roadmap items. Your account email is stored with submissions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Rating (1–5)</span>
            <select
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={feedbackRating}
              onChange={(e) => setFeedbackRating(e.target.value)}
              aria-label="Session rating"
            >
              <option value="">—</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <textarea
            className="min-h-[88px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="What worked well? What should we improve?"
            value={feedbackComment}
            onChange={(e) => setFeedbackComment(e.target.value)}
            maxLength={8000}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={feedbackSending || (!feedbackComment.trim() && !feedbackRating)}
            onClick={async () => {
              setFeedbackSending(true);
              try {
                const r = feedbackRating ? parseInt(feedbackRating, 10) : undefined;
                await submitPilotFeedback(Number.isFinite(r) ? r : undefined, feedbackComment.trim());
                toast.success('Thanks — feedback saved.');
                setFeedbackComment('');
                setFeedbackRating('');
              } catch (e) {
                toast.error(e.message || 'Could not send feedback.');
              } finally {
                setFeedbackSending(false);
              }
            }}
          >
            {feedbackSending ? 'Sending…' : 'Send feedback'}
          </Button>
        </CardContent>
      </Card>

      <div className="aerta-stagger grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Asset severity mix</CardTitle>
            <CardDescription>Distribution from the latest risk report (by asset counts).</CardDescription>
          </CardHeader>
          <CardContent>
            {severityData.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No risk report yet — run a scan on the Risk page to populate charts.
              </p>
            ) : (
              <div className="mx-auto w-full max-w-lg py-1">
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
                      <Pie
                        data={severityData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={88}
                        paddingAngle={2}
                        label={false}
                        isAnimationActive
                        animationDuration={900}
                        animationEasing="ease-out"
                      >
                        {severityData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} stroke="rgba(0,0,0,0.12)" />
                        ))}
                      </Pie>
                      <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(v) => [v, 'Assets']} />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        layout="horizontal"
                        wrapperStyle={{ paddingTop: 12 }}
                        formatter={(value) => {
                          const item = severityData.find((d) => d.name === value);
                          const total = severityData.reduce((sum, d) => sum + d.value, 0);
                          if (!item || !total) return value;
                          const pct = Math.round((item.value / total) * 100);
                          return `${value} (${pct}%)`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Defense posture score</CardTitle>
            <CardDescription>
              0–100 (higher is better). Derived from predicted threat likelihoods.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-center text-3xl font-bold text-card-foreground">
              {Math.round(forecast?.defense_score ?? 0)}
              <span className="text-lg font-normal text-muted-foreground"> / 100</span>
            </p>
            <div className="h-[120px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={defenseBarData} layout="vertical" margin={{ left: 0, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border opacity-40" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                  <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(v) => [v, 'Score']} />
                  <Bar
                    dataKey="score"
                    fill="#3b82f6"
                    radius={[0, 6, 6, 0]}
                    barSize={28}
                    isAnimationActive
                    animationDuration={800}
                    animationEasing="ease-out"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top predicted threats</CardTitle>
          <CardDescription>
            Ranked paths from the forecast engine — likelihood shown as % of the model range.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {threatBarData.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No forecast data — ensure registry + forecast pipeline ran, or open Forecasts page.
            </p>
          ) : (
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={threatBarData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border opacity-40" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fill: '#a1a1aa', fontSize: 11 }}
                  />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(v) => [`${v}%`, 'Likelihood']}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                  />
                  <Bar
                    dataKey="likelihoodPct"
                    name="Likelihood %"
                    fill="#0ea5e9"
                    radius={[0, 4, 4, 0]}
                    isAnimationActive
                    animationDuration={900}
                    animationEasing="ease-out"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top risk asset</CardTitle>
          <CardDescription>Highest priority asset from the latest report.</CardDescription>
        </CardHeader>
        <CardContent>
          {risk?.top_asset ? (
            <div className="text-sm text-card-foreground">
              <p>
                <strong>Host:</strong> {risk.top_asset.hostname || risk.top_asset.ip || 'n/a'}
              </p>
              <p>
                <strong>Type:</strong> {risk.top_asset.device_type || 'unknown'}
              </p>
              <p>
                <strong>Likelihood:</strong> {risk.top_asset.breach_likelihood || 'unknown'}
              </p>
              <p>
                <strong>Top CVE:</strong> {risk.top_asset.top_cve || 'none'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No risk report yet. Trigger a risk scan from the Risk page.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
